import type {
  Bounds,
  Vec2,
} from '../shared/types';
import {
  boundsSize,
  isFiniteBounds,
} from './geometry';

/**
 * Maps world coordinates (millimetres, Y-up) to screen pixels (Y-down).
 *   screenX = worldX * scale + tx
 *   screenY = -worldY * scale + ty
 */
export class Viewport {
  scale = 0.1; // pixels per mm
  tx = 0;
  ty = 0;

  worldToScreen(p: Vec2): Vec2 {
    return { x: p.x * this.scale + this.tx, y: -p.y * this.scale + this.ty };
  }

  screenToWorld(s: Vec2): Vec2 {
    return { x: (s.x - this.tx) / this.scale, y: -(s.y - this.ty) / this.scale };
  }

  /** Millimetres spanned by one screen pixel at the current zoom. */
  mmPerPx(): number { return 1 / this.scale; }

  /** Convert a screen-pixel length to world millimetres (scale-independent of pan). */
  pxToMm(px: number): number { return px / this.scale; }

  /** Convert a world-millimetre length to screen pixels. */
  mmToPx(mm: number): number { return mm * this.scale; }

  /** Zoom by a factor, keeping the world point under `pivot` (screen px) fixed. */
  zoomAt(pivot: Vec2, factor: number): void {
    const world = this.screenToWorld(pivot);
    this.scale = clamp(this.scale * factor, 1e-4, 1000);
    this.tx = pivot.x - world.x * this.scale;
    this.ty = pivot.y + world.y * this.scale;
  }

  pan(dx: number, dy: number): void {
    this.tx += dx;
    this.ty += dy;
  }

  /** Fit the given world bounds into a viewport of the given pixel size. */
  fit(bounds: Bounds, width: number, height: number, margin = 0.08): void {
    if (!isFiniteBounds(bounds) || width <= 0 || height <= 0) return;
    const size = boundsSize(bounds);
    const sx = (width * (1 - margin * 2)) / size.x;
    const sy = (height * (1 - margin * 2)) / size.y;
    this.scale = clamp(Math.min(sx, sy), 1e-4, 1000);
    const cx = (bounds.min.x + bounds.max.x) / 2;
    const cy = (bounds.min.y + bounds.max.y) / 2;
    this.tx = width / 2 - cx * this.scale;
    this.ty = height / 2 + cy * this.scale;
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
